/**
 * SecureFileLoadBanner — SDE (s112 SF-2).
 *
 * Proactive, dismissable "load your secure file" banner for a RETURNING planner: a Secure
 * File exists on the server (clientData.secureFileSetupAt is set) but its values are NOT
 * loaded into the in-memory store this session (session.isSetupComplete is false) and the
 * silent auto-reconnect in SecureFileOpenProvider didn't fire — e.g. a new device, a revoked
 * file-permission grant, a passphrase-protected file, or Safari/Firefox. It reminds the
 * planner where the file is kept (secure_file_location, read from the Where-Is-Everything
 * aggregate — the same source as SecureFileStatusCard) and offers a one-click Open, instead
 * of leaving only the quiet per-field "Open file" links scattered across Cat A surfaces.
 *
 * Dismissable ONCE PER SESSION (local state at the persistent shell mount; resets on a full
 * reload / next login). Loading the file (or creating one inline) flips session.isSetupComplete
 * true, which also hides the banner.
 *
 * Distinct from SecureFileBanner (the amber UNSAVED-changes / write-side prompt).
 * Must be mounted inside SensitiveDataSessionProvider + SecureFileOpenProvider.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, FolderOpen, X } from "lucide-react";
import type { Client } from "@shared/schema";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { useSensitiveDataSession } from "@/lib/sensitive-data-session-provider";
import { useSecureFileOpen } from "@/components/SecureFileOpenProvider";
import { SAGE, WARM } from "@shared/brand";

export function SecureFileLoadBanner({ clientData }: { clientData: Pick<Client, "id" | "secureFileSetupAt"> }) {
  const setupComplete = !!clientData.secureFileSetupAt;
  const session = useSensitiveDataSession();
  const { openFile } = useSecureFileOpen();
  const [dismissed, setDismissed] = useState(false);

  // Only fetch the location reminder when the banner could actually show.
  const { data: wie = [] } = useQuery<Array<{ fieldKey?: string | null; location?: string }>>({
    queryKey: ["/api/clients", clientData.id, CATEGORY_SLUGS.WHERE_IS_EVERYTHING],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientData.id}/where-is-everything`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientData.id && setupComplete && !session.isSetupComplete,
  });
  const location = wie.find((r) => r.fieldKey === "secure_file_location")?.location?.trim();

  // Show only for a returning planner whose file isn't loaded into memory this session.
  if (!setupComplete || session.isSetupComplete || dismissed) return null;

  return (
    <div
      role="status"
      data-testid="secure-file-load-banner"
      className="flex items-center justify-between gap-3 px-4 py-2.5 border-b"
      style={{ backgroundColor: SAGE[50], borderColor: WARM.stone }}
    >
      <div className="flex items-center gap-2 text-sm" style={{ color: "#3F5A42" }}>
        <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: SAGE[600] }} />
        <span>
          {location ? (
            <>
              Your secure file is kept at <span className="font-semibold">{location}</span>. Load
              it to view your sensitive values.
            </>
          ) : (
            <>Your secure file isn't loaded. Load it to view your sensitive values.</>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={openFile}
          data-testid="secure-file-load-banner-open"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: SAGE[600] }}
        >
          <FolderOpen className="h-3.5 w-3.5" />
          Open file
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="secure-file-load-banner-dismiss"
          aria-label="Dismiss"
          className="inline-flex items-center rounded-md p-1.5 transition-colors hover:bg-black/5"
          style={{ color: "#3F5A42" }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
